import useChoices from "@hooks/useChoices";
import { useTranslation } from "react-i18next";
import { statusList } from "@helper/healthState";
import InfoBox from "@components/InfoBox/InfoBox";
import { useState } from "react";
import { refreshTreeHealth } from "../services/treeCrud";
import { formatDistanceToNow } from "date-fns";
import { de, enUS } from "date-fns/locale";
import RefreshIcon from "@mui/icons-material/Refresh";
import { IconButton, Tooltip, CircularProgress } from "@mui/material";
import { useQueryClient } from "react-query";
import { useSelector } from "react-redux";

export const DetailListItem = ({ title, value }) => {
  const { t } = useTranslation();
  return (
    <div className="entity-detail-data-row">
      <div className="entity-detail-data-label">{t(`${title}`)}</div>
      <div className="entity-detail-data-value">{value}</div>
    </div>
  );
};

const DEMO_TREE_ID = 1909;
const DEMO_USER_EMAIL = "timelapse@treesense.net";
const DEMO_WIKI_URL =
  "https://www.treesense.net/wiki_post/der-versuch-hinter-dem-zeitraffer-des-zitronenbaums/";

const TreeInfo = ({ tree, infoAction = null, isPublic = false }) => {
  const { t, i18n } = useTranslation();
  const [locations] = useChoices("location");
  const [phases] = useChoices("growth_phase");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState(null);
  const queryClient = useQueryClient();

  const authUser = useSelector((state: any) => state.auth?.user);
  const currentUserEmail = authUser?.email?.toLowerCase?.();

  const shouldShowWiki =
    Number(tree?.id) === DEMO_TREE_ID && currentUserEmail === DEMO_USER_EMAIL.toLowerCase();

  const handleRefreshHealth = async () => {
    if (!tree?.id || isRefreshing) return;

    setIsRefreshing(true);
    setRefreshError(null);

    try {
      const response = await refreshTreeHealth(tree.id);

      // Immediately update and refetch all tree-related queries
      if (response?.tree) {
        // Update the cache for both number and string IDs (URL param is string)
        queryClient.setQueryData(["tree", tree.id], response.tree);
        queryClient.setQueryData(["tree", String(tree.id)], response.tree);

        // Force refetch of all tree-related queries to ensure consistency
        // Use ["tree"] without ID to refetch ALL tree detail queries
        await Promise.all([
          queryClient.refetchQueries(["tree"]),
          queryClient.refetchQueries(["trees-paginated"]),
          queryClient.refetchQueries(["trees"]),
        ]);
      }
    } catch (error) {
      console.error("Failed to refresh tree health:", error);
      setRefreshError(t("refresh_health_error") || "Failed to refresh health data");
    } finally {
      setIsRefreshing(false);
    }
  };

  const formatLastUpdated = (lastUpdated) => {
    if (!lastUpdated) return null;
    try {
      const locale = i18n.language === "de" ? de : enUS;
      const distance = formatDistanceToNow(new Date(lastUpdated), {
        addSuffix: true,
        locale,
      });

      // For German, prepend "Aktualisiert" to make it read better
      if (i18n.language === "de") {
        return `Aktualisiert ${distance}`;
      }

      return `Updated ${distance}`;
    } catch (error) {
      return null;
    }
  };

  if (!tree) return null;

  return (
    <>
      <div className="entity-detail-section-header">
        <h3 className="entity-detail-section-title">{t("tree")}</h3>
        {infoAction}
      </div>

      <div data-testid="tree-detail-row" className="entity-detail-data">
        {tree?.name && <DetailListItem title="name" value={tree.name} />}
        {tree?.tree_cadastre_no && (
          <DetailListItem
            title="tree_cadastre_no"
            value={tree && tree.tree_cadastre_no ? tree.tree_cadastre_no : ""}
          />
        )}
        {locations && tree?.location && (
          <DetailListItem
            title="location"
            value={
              {
                1: locations && t(locations[0].value),
                2: locations && t(locations[1].value),
              }[tree.location]
            }
          />
        )}
        <DetailListItem
          title="treespecies"
          value={tree && tree.species ? `${tree.species.name} | ${tree.species.de_name}` : ""}
        />
        {tree?.height && (
          <DetailListItem title="height" value={tree?.height ? tree.height + " m" : ""} />
        )}
        {tree?.crown_diameter && (
          <DetailListItem
            title="crown_diameter"
            value={tree?.crown_diameter ? tree.crown_diameter + " m" : ""}
          />
        )}
        {tree?.trunk_circumference && (
          <DetailListItem
            title="trunk_circumference"
            value={tree && tree.trunk_circumference ? tree.trunk_circumference + " cm" : ""}
          />
        )}
        {tree?.planting_year && (
          <DetailListItem
            title="planting_year"
            value={tree?.planting_year ? tree.planting_year : ""}
          />
        )}
        {phases && (
          <DetailListItem
            title="phase"
            value={
              {
                1: phases && t(phases[0].value),
                2: phases && t(phases[1].value),
                3: phases && t(phases[2].value),
              }[tree.phase]
            }
          />
        )}
        {shouldShowWiki && (
          <DetailListItem
            title="Wiki"
            value={
              <a href={DEMO_WIKI_URL} target="_blank" rel="noopener noreferrer">
                {t("tree_wiki")}
              </a>
            }
          />
        )}
        <div className="entity-detail-data-row">
          <div className="entity-detail-data-label">{t("health_state")}</div>
          <div className="entity-detail-data-value d-flex align-items-center justify-content-between gap-3">
            <div className="d-flex flex-column">
              <span>
                {
                  {
                    0: t(statusList[0].value),
                    1: t(statusList[1].value),
                    2: t(statusList[2].value),
                    3: t(statusList[3].value),
                    4: t(statusList[4].value),
                  }[tree?.health_state ?? 3]
                }
              </span>
              {tree?.last_updated && (
                <small style={{ color: "#666", fontSize: "0.85em" }}>
                  {formatLastUpdated(tree.last_updated)}
                </small>
              )}
              {refreshError && (
                <small style={{ color: "#d32f2f", fontSize: "0.85em" }}>{refreshError}</small>
              )}
            </div>
            <div className="d-flex align-items-center gap-2">
              {!isPublic && (
                <Tooltip title={t("refresh_health") || "Refresh health data"}>
                  <IconButton
                    onClick={handleRefreshHealth}
                    disabled={isRefreshing}
                    size="small"
                    color="primary"
                  >
                    {isRefreshing ? (
                      <CircularProgress size={20} />
                    ) : (
                      <RefreshIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
              )}
              <InfoBox
                infoTitle={t("health_state")}
                infoContent={
                  <>
                    <p>
                      <strong>{t("health_state_info.heading")}</strong>
                    </p>
                    <p>{t("health_state_info.intro")}</p>
                    <ul>
                      <li>
                        {t("health_state_info.states.green_label")}{" "}
                        {t("health_state_info.states.green_help")}
                      </li>
                      <li>
                        {t("health_state_info.states.yellow_label")}{" "}
                        {t("health_state_info.states.yellow_help")}
                      </li>
                      <li>
                        {t("health_state_info.states.red_label")}{" "}
                        {t("health_state_info.states.red_help")}
                      </li>
                      <li>
                        {t("health_state_info.states.gray_label")}{" "}
                        {t("health_state_info.states.gray_help")}
                      </li>
                      <li>
                        {t("health_state_info.states.blue_label")}{" "}
                        {t("health_state_info.states.blue_help")}
                      </li>
                    </ul>
                    <p>
                      <em>{t("health_state_info.note")}</em>
                    </p>
                  </>
                }
              />
            </div>
          </div>
        </div>
      </div>
      {tree?.soil && (
        <>
          <div className="entity-detail-section-header mt-4">
            <h3 className="entity-detail-section-title">{t("soil")}</h3>
            <InfoBox
              infoTitle={t("soil")}
              infoContent={
                <>
                  <p>
                    <strong>{t("soil_info.heading")}</strong>
                  </p>
                  <p>{t("soil_info.intro")}</p>
                  <ul>
                    <li>
                      <strong>{t("soil_info.items.pwp_label")}</strong>{" "}
                      {t("soil_info.items.pwp_help")}
                    </li>
                    <li>
                      <strong>{t("soil_info.items.efc_label")}</strong>{" "}
                      {t("soil_info.items.efc_help")}
                    </li>
                    <li>
                      <strong>{t("soil_info.items.fc_label")}</strong>{" "}
                      {t("soil_info.items.fc_help")}
                    </li>
                  </ul>
                  <p>
                    <em>{t("soil_info.note")}</em>
                  </p>
                </>
              }
            />
          </div>
          <div className="entity-detail-data">
            <DetailListItem title="soil_name" value={tree?.soil.name || "-"} />
            <div className="entity-detail-data-row">
              <div className="entity-detail-data-label">{t("permanent_wilting_point")}</div>
              <div className="entity-detail-data-value d-flex align-items-center justify-content-between gap-3">
                <span>{tree?.soil.pwp + " %" || "-"}</span>
                <InfoBox
                  infoTitle={t("permanent_wilting_point")}
                  infoContent={
                    <>
                      <p>
                        <strong>{t("pwp_info.heading")}</strong>
                      </p>
                      <p>{t("pwp_info.intro")}</p>
                      <p>{t("pwp_info.when_reached")}</p>
                      <ul>
                        <li>{t("pwp_info.bullets.a")}</li>
                        <li>{t("pwp_info.bullets.b")}</li>
                        <li>{t("pwp_info.bullets.c")}</li>
                      </ul>
                      <p>
                        <em>{t("pwp_info.note")}</em>
                      </p>
                    </>
                  }
                />
              </div>
            </div>
            <div className="entity-detail-data-row">
              <div className="entity-detail-data-label">{t("effective_field_capacity")}</div>
              <div className="entity-detail-data-value d-flex align-items-center justify-content-between gap-3">
                <span>{tree?.soil.max_nf + " %" || "-"}</span>
                <InfoBox
                  infoTitle={t("effective_field_capacity")}
                  infoContent={
                    <>
                      <p>
                        <strong>{t("efc_info.heading")}</strong>
                      </p>
                      <p>{t("efc_info.intro")}</p>
                      <p>{t("efc_info.formula_intro")}</p>
                      <ul>
                        <li>{t("efc_info.bullets.a")}</li>
                        <li>{t("efc_info.bullets.b")}</li>
                        <li>{t("efc_info.bullets.c")}</li>
                      </ul>
                      <p>
                        <em>{t("efc_info.note")}</em>
                      </p>
                    </>
                  }
                />
              </div>
            </div>
            <div className="entity-detail-data-row">
              <div className="entity-detail-data-label">{t("field_capacity")}</div>
              <div className="entity-detail-data-value d-flex align-items-center justify-content-between gap-3">
                <span>{tree.soil.max_f + " %" || "-"}</span>
                <InfoBox
                  infoTitle={t("field_capacity")}
                  infoContent={
                    <>
                      <p>
                        <strong>{t("fc_info.heading")}</strong>
                      </p>
                      <p>{t("fc_info.intro")}</p>
                      <p>{t("fc_info.after_rain")}</p>
                      <ul>
                        <li>{t("fc_info.bullets.a")}</li>
                        <li>{t("fc_info.bullets.b")}</li>
                        <li>{t("fc_info.bullets.c")}</li>
                      </ul>
                      <p>
                        <em>{t("fc_info.note")}</em>
                      </p>
                    </>
                  }
                />
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default TreeInfo;
